/**
 * CUSTOM ELEMENT
 * Product Gallery
 *
 * Desktop arrow and dot navigation for the gallery's scroll-snap container. Each control is
 * wired only if present, so the grid treatment (which renders neither) costs nothing.
 *
 * With `data-autoplay-speed` (px/sec) the track also drifts left at a constant velocity —
 * linear, no easing, matching the topbar marquee.
 *
 * With `data-loop` the arrows and dots wrap instead of stopping at the ends. While the drift
 * plays they always wrap, since the track itself is already endless.
 */
class ProductGalleryElement extends HTMLElement {
  static CONTAINER_SELECTOR = '[data-role="base-container"]'
  static PREVIOUS_SELECTOR = '[data-role="gallery-previous"]'
  static NEXT_SELECTOR = '[data-role="gallery-next"]'
  static DOT_SELECTOR = '[data-role="gallery-dot"]'
  static LIGHTBOX_SELECTOR = '[data-id="lightbox"]'

  // md is 1024px in this theme, not Tailwind's default 768 — autoplay is a md+ difference only,
  // so the mobile treatment stays byte-identical to the grid control.
  static DESKTOP_QUERY = '(min-width: 1024px)'
  static RESUME_DELAY = 2000
  // Caps catch-up after a stalled frame so a hitch cannot teleport the track.
  static MAX_DELTA = 0.05

  constructor() {
    super()
    this.container = null
    this.previousElement = null
    this.nextElement = null
    this.dots = []
    this.scrollFrame = null
    this.lastActive = -1
    this.loop = false

    this.speed = 0
    this.playing = false
    this.clone = null
    this.loopWidth = 0
    this.frame = null
    this.lastTime = null
    // scrollLeft is quantised to device pixels, so sub-pixel steps accumulate here instead.
    this.position = 0
    this.pauseReasons = new Set()
    this.resumeTimer = null
    this.desktopQuery = null
    this.resizeObserver = null
    this.intersectionObserver = null
    this.lightboxObserver = null

    // Bound once so add/removeEventListener stay symmetric.
    this.handleScrollStep = (e) => {
      const forward = e.currentTarget.dataset.role === 'gallery-next'
      this.holdForManualNav()
      this.rebaseOffClone()
      this.scrollToIndex(this.activeIndex() + (forward ? 1 : -1))
    }

    this.handleDotClick = (e) => {
      this.holdForManualNav()
      this.rebaseOffClone()
      this.scrollToIndex(parseInt(e.currentTarget.dataset.index, 10))
    }

    this.handleScroll = () => {
      // A movement we did not write (drag, wheel, smooth scrollTo) invalidates the accumulator.
      if (this.playing && Math.abs(this.container.scrollLeft - this.position) > 2) {
        this.position = this.container.scrollLeft
      }
      if (this.scrollFrame) return
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null
        this.handleUpdateControls()
      })
    }

    this.handleFrame = (time) => {
      this.frame = requestAnimationFrame(this.handleFrame)

      if (this.pauseReasons.size) {
        this.lastTime = null
        return
      }

      // First frame of a play cycle only re-syncs; a resumed pause must not replay its gap.
      if (this.lastTime === null) {
        this.lastTime = time
        this.position = this.container.scrollLeft
        return
      }

      const delta = Math.min((time - this.lastTime) / 1000, ProductGalleryElement.MAX_DELTA)
      this.lastTime = time

      if (!this.loopWidth) this.measure()
      if (!this.loopWidth) return

      this.position += this.speed * delta
      // Modulo, not subtraction: one dropped frame at a high speed can overshoot a whole loop.
      if (this.position >= this.loopWidth) this.position %= this.loopWidth
      this.container.scrollLeft = this.position
    }

    this.handleBreakpoint = () => {
      if (this.desktopQuery?.matches) this.startAutoplay()
      else this.stopAutoplay()
    }

    this.handleMouseEnter = () => this.pause('hover')
    this.handleMouseLeave = () => this.resume('hover')
    this.handleFocusIn = () => this.pause('focus')
    this.handleFocusOut = () => this.resume('focus')
    this.handleVisibility = () => {
      if (document.hidden) this.pause('hidden')
      else this.resume('hidden')
    }
  }

  activeIndex() {
    if (!this.container?.clientWidth) return 0
    return Math.round(this.container.scrollLeft / this.container.clientWidth)
  }

  /** Real slides only: autoplay appends a trailing clone, which must never be a wrap target. */
  slideCount() {
    const count = this.container?.children.length ?? 0
    return this.clone ? count - 1 : count
  }

  /** Scrolls the track only — scrollIntoView would also scroll the page. */
  scrollToIndex(index) {
    const cells = this.container?.children
    if (!cells?.length) return

    const total = this.slideCount()
    let target
    let wrapped = false

    if (this.clone && index === total) {
      // Next from the last slide: glide onto the clone, which is pixel-identical to the first.
      target = total
    } else if ((this.loop || this.clone) && total > 1) {
      // The drift already loops, so manual nav wraps while it plays even without data-loop.
      // JS % keeps the sign, so bias by total to land in range from either end.
      target = ((index % total) + total) % total
      wrapped = target !== index
    } else {
      target = Math.max(0, Math.min(index, total - 1))
    }

    const cell = cells[target]
    if (!cell) return
    // A smooth wrap pans the whole track back and reads as a rewind, so the wrap jumps instead.
    this.container.scrollTo({ left: cell.offsetLeft, behavior: wrapped ? 'auto' : 'smooth' })
  }

  /** Moves a track sitting on the clone back onto the identical first slide, invisibly. */
  rebaseOffClone() {
    if (!this.clone || !this.loopWidth) return
    // Only when fully on it: a part-way offset minus loopWidth is negative and would clamp to 0.
    if (this.container.scrollLeft < this.loopWidth - 1) return
    // Inline scroll-behavior is 'auto' while playing, so this assignment jumps, not animates.
    this.container.scrollLeft -= this.loopWidth
    this.position = this.container.scrollLeft
  }

  handleUpdateControls() {
    if (!this.container) return
    const { scrollLeft, clientWidth, scrollWidth } = this.container

    // A looping track has no ends to disable against — while drifting, or by merchant setting.
    if (!this.playing && !this.loop) {
      this.previousElement?.toggleAttribute('disabled', scrollLeft <= 1)
      this.nextElement?.toggleAttribute('disabled', scrollLeft + clientWidth >= scrollWidth - 1)
    }

    // The trailing clone reads as one index past the last dot; fold it back onto the first.
    const total = this.dots.length
    const active = total ? this.activeIndex() % total : this.activeIndex()
    // Autoplay fires this every frame, so only touch the DOM when the slide actually changes.
    if (active === this.lastActive) return
    this.lastActive = active
    this.dots.forEach((dot, i) => {
      if (i === active) dot.setAttribute('aria-current', 'true')
      else dot.removeAttribute('aria-current')
    })
  }

  fetchChildren() {
    this.container = this.querySelector(ProductGalleryElement.CONTAINER_SELECTOR)
    this.previousElement = this.querySelector(ProductGalleryElement.PREVIOUS_SELECTOR)
    this.nextElement = this.querySelector(ProductGalleryElement.NEXT_SELECTOR)
    this.dots = Array.from(this.querySelectorAll(ProductGalleryElement.DOT_SELECTOR))
    this.loop = this.dataset.loop === 'true'
  }

  pause(reason) {
    this.pauseReasons.add(reason)
  }

  resume(reason) {
    this.pauseReasons.delete(reason)
  }

  /** Volley parity: a manual jump holds the drift briefly rather than cancelling it. */
  holdForManualNav() {
    if (!this.playing) return
    this.pause('manual')
    clearTimeout(this.resumeTimer)
    this.resumeTimer = setTimeout(() => this.resume('manual'), ProductGalleryElement.RESUME_DELAY)
  }

  /** The clone sits immediately after the originals, so its offset is the true loop width. */
  measure() {
    this.loopWidth = this.clone ? this.clone.offsetLeft : 0
  }

  setupAutoplay() {
    this.speed = parseFloat(this.dataset.autoplaySpeed)
    if (!(this.speed > 0)) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    this.desktopQuery = window.matchMedia(ProductGalleryElement.DESKTOP_QUERY)
    this.desktopQuery.addEventListener('change', this.handleBreakpoint)
    this.handleBreakpoint()
  }

  startAutoplay() {
    if (this.playing || !this.container) return
    const cells = Array.from(this.container.children)
    if (cells.length < 2) return

    // A copy of the first slide trails the set so the reset at loop width lands on identical
    // pixels. Carousel cells are basis-full, so one copy is exactly a viewport's worth.
    this.clone = cells[0].cloneNode(true)
    this.clone.removeAttribute('data-role')
    this.clone.removeAttribute('data-index')
    this.clone.setAttribute('aria-hidden', 'true')
    this.clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'))
    this.container.appendChild(this.clone)

    // Mandatory snapping fights a per-frame scrollLeft write, and html's global smooth
    // scroll-behavior would animate the wrap instead of jumping it.
    this.container.style.scrollSnapType = 'none'
    this.container.style.scrollBehavior = 'auto'

    this.playing = true
    this.pauseReasons.clear()
    this.lastTime = null
    this.measure()

    this.previousElement?.removeAttribute('disabled')
    this.nextElement?.removeAttribute('disabled')

    this.observeAutoplay()
    this.frame = requestAnimationFrame(this.handleFrame)
  }

  observeAutoplay() {
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', this.handleMouseEnter)
      this.addEventListener('mouseleave', this.handleMouseLeave)
    }
    this.addEventListener('focusin', this.handleFocusIn)
    this.addEventListener('focusout', this.handleFocusOut)
    document.addEventListener('visibilitychange', this.handleVisibility)

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.resume('offscreen')
      else this.pause('offscreen')
    })
    this.intersectionObserver.observe(this)

    this.resizeObserver = new ResizeObserver(() => this.measure())
    this.resizeObserver.observe(this.container)

    // lightbox.js drives its thumbnail indicator off this track's scroll events, so an open
    // lightbox would otherwise see the drift as the shopper scrubbing the gallery.
    const lightbox = document.querySelector(ProductGalleryElement.LIGHTBOX_SELECTOR)
    if (!lightbox) return
    this.lightboxObserver = new MutationObserver(() => {
      if (lightbox.dataset.ui === 'visible') this.pause('lightbox')
      else this.resume('lightbox')
    })
    this.lightboxObserver.observe(lightbox, { attributes: true, attributeFilter: ['data-ui'] })
  }

  stopAutoplay() {
    if (!this.playing) return
    this.playing = false

    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    clearTimeout(this.resumeTimer)
    this.resumeTimer = null
    this.pauseReasons.clear()

    this.removeEventListener('mouseenter', this.handleMouseEnter)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    this.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('focusout', this.handleFocusOut)
    document.removeEventListener('visibilitychange', this.handleVisibility)

    this.intersectionObserver?.disconnect()
    this.resizeObserver?.disconnect()
    this.lightboxObserver?.disconnect()
    this.intersectionObserver = null
    this.resizeObserver = null
    this.lightboxObserver = null

    this.rebaseOffClone()
    this.clone?.remove()
    this.clone = null
    this.loopWidth = 0

    this.container.style.scrollSnapType = ''
    this.container.style.scrollBehavior = ''
    // Removing the clone shrinks the scroll range; settle back onto a real slide.
    this.scrollToIndex(this.activeIndex())
    this.handleUpdateControls()
  }

  connectedCallback() {
    this.fetchChildren()
    if (!this.container) return
    if (!this.previousElement && !this.nextElement && !this.dots.length) return

    this.previousElement?.addEventListener('click', this.handleScrollStep)
    this.nextElement?.addEventListener('click', this.handleScrollStep)
    this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick))
    this.container.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleUpdateControls()

    this.setupAutoplay()
  }

  disconnectedCallback() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)
    this.stopAutoplay()
    this.desktopQuery?.removeEventListener('change', this.handleBreakpoint)
    this.previousElement?.removeEventListener('click', this.handleScrollStep)
    this.nextElement?.removeEventListener('click', this.handleScrollStep)
    this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick))
    this.container?.removeEventListener('scroll', this.handleScroll)
  }
}

customElements.define('x-product-gallery', ProductGalleryElement)
